import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { ShareItemCardCover, ShareItemCardFeed } from "@/components/share/ShareItemCard";
import type { ListingCardFragment, ListingCardImageFragment } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { ListingReference } from "./ListingReference";
import { LISTING_STATE_LABELS } from "./listing-state";

export type ListingTile = ListingCardFragment & Partial<ListingCardImageFragment>;

export function ListingCard({
  listing,
  idPrefix,
  index = 0,
  variant = "cover",
  to = `/share/listings/${listing.id}`,
}: {
  listing: ListingTile;
  idPrefix?: string;
  index?: number;
  variant?: "cover" | "feed";
  to?: string;
}) {
  const intl = useIntl();
  const title = listing.name || intl.formatMessage({ id: "share.listings.untitled", defaultMessage: "Ohne Titel" });
  const deadline = formatDateTime(listing.deadline, intl.locale);
  const Card = variant === "feed" ? ShareItemCardFeed : ShareItemCardCover;
  return (
    <Link
      to={to}
      aria-label={title}
      className={`group block w-full ${variant === "cover" ? "max-w-64" : ""} rounded-md transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30`}
    >
      <Card
        idPrefix={idPrefix}
        item={{
          id: listing.id,
          name: title,
          shortDescription: listing.description,
          mainImage: listing.mainImage,
        }}
        index={index}
        {...(variant === "cover" ? { heroImageSrc: "/heroes/share.png", imageFallback: "details" as const } : {})}
        badge={listing.state ? <FormattedMessage {...LISTING_STATE_LABELS[listing.state]} /> : ""}
        footer={
          <span className="flex flex-col text-ink-muted">
            <span>
              {deadline ? (
                <FormattedMessage id="share.listings.deadlineOn" defaultMessage="Frist {date}" values={{ date: deadline }} />
              ) : (
                <FormattedMessage id="share.listings.noDeadline" defaultMessage="Keine Frist" />
              )}
            </span>
            <ListingReference reference={listing.reference} />
          </span>
        }
      />
    </Link>
  );
}
